/**
 * @fileoverview Share Portal Authentication Store
 *
 * Manages share portal authentication for external users.
 * Uses session tokens (not JWT) stored in sessionStorage (not localStorage).
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import * as sharePortalApi from '@/api/sharePortal'
import { setShareSessionToken, getShareSessionToken, clearShareSessionToken } from '@/api/client'

/**
 * @typedef {Object} ShareInfo
 * @property {string} share_id - Share UUID
 * @property {string} lab_id - Prefixed lab ID (lab-xxx)
 * @property {string} lab_name - Lab name
 * @property {string} description - Share description
 * @property {string} expires_at - ISO timestamp of expiration
 * @property {boolean} is_active - Whether share is active
 * @property {boolean} requires_authentication - Always true
 */

/**
 * Pinia store for share portal authentication
 *
 * @example
 * import { useShareSessionStore } from '@/stores/shareSession'
 *
 * const shareStore = useShareSessionStore()
 * await shareStore.loadShareInfo('550e8400-e29b-41d4-a716-446655440000')
 * await shareStore.login(shareId, username, password)
 */
export const useShareSessionStore = defineStore('shareSession', () => {
  // State
  /** @type {import('vue').Ref<string|null>} */
  const shareId = ref(null)

  /** @type {import('vue').Ref<string|null>} */
  const labId = ref(null)

  /** @type {import('vue').Ref<string|null>} */
  const labName = ref(null)

  /** @type {import('vue').Ref<Date|null>} */
  const expiresAt = ref(null)

  /** @type {import('vue').Ref<boolean>} */
  const isAuthenticated = ref(false)

  /** @type {import('vue').Ref<boolean>} */
  const isLoading = ref(false)

  /** @type {import('vue').Ref<string|null>} */
  const error = ref(null)

  // Getters

  /**
   * Calculate seconds remaining until session expiration
   * @returns {number} Seconds until expiry, 0 if expired or no session
   */
  const sessionTimeRemaining = computed(() => {
    if (!expiresAt.value) {
      return 0
    }

    const now = new Date()
    const expiry = new Date(expiresAt.value)
    const diffMs = expiry.getTime() - now.getTime()
    const diffSeconds = Math.floor(diffMs / 1000)

    return diffSeconds > 0 ? diffSeconds : 0
  })

  /**
   * Check if session is expired
   * @returns {boolean} True if session is expired, false if not expired or unknown
   */
  const isExpired = computed(() => {
    // If expiresAt is not set, we can't determine expiration - assume not expired
    // The token existence check in checkSession() handles missing tokens
    if (!expiresAt.value) {
      return false
    }

    return sessionTimeRemaining.value <= 0
  })

  // Actions

  /**
   * Load public share information (no authentication required)
   *
   * Fetches share details to display on the share portal login page.
   * This is a public endpoint.
   *
   * @param {string} id - Share UUID
   * @returns {Promise<ShareInfo>} Share information
   * @throws {Error} If share not found or is expired
   * @example
   * const shareStore = useShareSessionStore()
   * const info = await shareStore.loadShareInfo('550e8400-e29b-41d4-a716-446655440000')
   * console.debug(`Lab: ${info.lab_name}`)
   */
  async function loadShareInfo(id) {
    try {
      isLoading.value = true
      error.value = null

      const info = await sharePortalApi.getShareInfo(id)

      // Update state with share info
      shareId.value = info.share_id
      labId.value = info.lab_id
      labName.value = info.lab_name

      // Also store in sessionStorage for persistence across page refreshes
      sessionStorage.setItem('share_lab_id', info.lab_id)
      sessionStorage.setItem('share_lab_name', info.lab_name)

      return info
    } catch (err) {
      error.value = err.message || 'Failed to load share information'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Authenticate with share credentials
   *
   * Logs in external user with Guacamole credentials and stores session token.
   * Session token is stored in sessionStorage (not localStorage).
   *
   * @param {string} id - Share UUID
   * @param {string} username - Guacamole username
   * @param {string} password - Guacamole password
   * @returns {Promise<void>}
   * @throws {Error} If authentication fails
   * @example
   * try {
   *   await shareStore.login(
   *     shareId,
   *     'share-550e8400@labs.autolabs.com',
   *     'provided-password'
   *   )
   *   // User is now authenticated
   * } catch (error) {
   *   console.error('Login failed:', error.message)
   * }
   */
  async function login(id, username, password) {
    try {
      isLoading.value = true
      error.value = null

      // Authenticate with share portal
      const authResponse = await sharePortalApi.authenticateShare(id, {
        username,
        password
      })

      // Store session token and share ID in sessionStorage
      setShareSessionToken(authResponse.session_token, id)

      // Update state - shareId and labName are set by loadShareInfo, keep them
      // Auth response only has session_token and expires_at
      shareId.value = id
      expiresAt.value = new Date(authResponse.expires_at)
      isAuthenticated.value = true
    } catch (err) {
      error.value = err.message || 'Authentication failed'
      // Clear any existing session
      clearSession()
      throw err
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Logout and clear share session
   *
   * Removes session token from sessionStorage and resets state.
   *
   * @example
   * shareStore.logout()
   * // Session cleared, user must re-authenticate
   */
  function logout() {
    clearSession()
  }

  /**
   * Check if current session is still valid
   *
   * Verifies that session token exists and hasn't expired.
   * Returns false if session is invalid.
   *
   * @returns {boolean} True if session is valid
   * @example
   * if (!shareStore.checkSession()) {
   *   // Session expired, redirect to login
   *   router.push(`/share/${shareId}/login`)
   * }
   */
  function checkSession() {
    const token = getShareSessionToken()

    if (!token) {
      clearSession()
      return false
    }

    // If we have a token but isAuthenticated is false, restore state
    // This handles page refresh where Pinia state is lost but sessionStorage persists
    if (!isAuthenticated.value) {
      const storedShareId = sessionStorage.getItem('share_session_id')
      if (storedShareId) {
        shareId.value = storedShareId
        isAuthenticated.value = true

        // Also restore lab info if available
        const storedLabId = sessionStorage.getItem('share_lab_id')
        const storedLabName = sessionStorage.getItem('share_lab_name')
        if (storedLabId) {
          labId.value = storedLabId
        }
        if (storedLabName) {
          labName.value = storedLabName
        }
      }
    }

    if (isExpired.value) {
      clearSession()
      return false
    }

    return true
  }

  /**
   * Clear session state and token
   *
   * Internal helper function to reset all session data.
   */
  function clearSession() {
    clearShareSessionToken()
    sessionStorage.removeItem('share_lab_id')
    sessionStorage.removeItem('share_lab_name')
    shareId.value = null
    labId.value = null
    labName.value = null
    expiresAt.value = null
    isAuthenticated.value = false
    error.value = null
  }

  return {
    // State
    shareId,
    labId,
    labName,
    expiresAt,
    isAuthenticated,
    isLoading,
    error,

    // Getters
    sessionTimeRemaining,
    isExpired,

    // Actions
    loadShareInfo,
    login,
    logout,
    checkSession
  }
})
